import { v4 as uuid } from 'uuid';
import { action, observable, makeObservable } from 'mobx';
import superagent from 'superagent'; // eslint-disable-line no-unused-vars
import { ApiClient as PublicApiClient } from 'public-api/codegen/api';
import { convertHeicToJpeg } from 'helpers/utils';

type UploadedFileOptions = {
  api: PublicApiClient;
  file: File;
  onUploaded?: (fileUpload: Backend.FileUpload | BackendPublic.FileUploadRequest, uploadedFile: IUploadedFile) => void;
};

/**
 * Uploaded file is used for uploading files in circumvented way using direct S3 upload.
 */
class UploadedFile implements IUploadedFile {
  @observable rawFile: File;
  @observable id: string;
  @observable percent: number = 0;
  @observable isUploaded: boolean = false;
  @observable isUploading: boolean = false;
  @observable isFailed: boolean = false;
  @observable errorCode: string | null = null;

  private api: PublicApiClient;
  private uploadRequest?: superagent.SuperAgentRequest;

  onUploaded?: (fileUpload: Backend.FileUpload | BackendPublic.FileUploadRequest, uploadedFile: IUploadedFile) => void;

  constructor({ api, file, onUploaded }: UploadedFileOptions) {
    makeObservable(this);
    this.id = uuid();
    this.api = api;
    this.rawFile = file;

    this.onUploaded = onUploaded;
  }

  @action.bound abort() {
    if (this.uploadRequest !== undefined) {
      this.uploadRequest.abort();
    }
  }

  @action.bound destroy() {
    this.abort();
  }

  private static uploadToS3(file: File, fileUploadRequest: BackendPublic.FileUploadCreateRequest) {
    const formData = new FormData();

    formData.append('key', fileUploadRequest.s3_key);
    formData.append('x-amz-algorithm', fileUploadRequest.s3_x_amz_algorithm);
    formData.append('x-amz-credential', fileUploadRequest.s3_x_amz_credential);
    formData.append('x-amz-date', fileUploadRequest.s3_x_amz_date);
    formData.append('policy', fileUploadRequest.s3_policy);
    formData.append('x-amz-signature', fileUploadRequest.s3_x_amz_signature);
    formData.append('acl', 'public-read');
    formData.append('Content-Type', fileUploadRequest.content_type);
    formData.append('Content-Disposition', fileUploadRequest.content_disposition);
    formData.append('file', file);

    return superagent.post(`${fileUploadRequest.s3_url}`).send(formData);
  }

  @action.bound async upload() {
    if (!this.isUploaded && !this.isUploading) {
      this.isUploading = true;
      this.isUploaded = false;
      this.isFailed = false;
      this.errorCode = null;

      if (this.rawFile.type === 'image/heic') {
        try {
          this.rawFile = await convertHeicToJpeg(this.rawFile);
        } catch (e: any) {
          window.Sentry.captureException(e);
        }
      }

      const fileUploadRequest = await this.api.FilesFileUploadRequestApi.create({
        request: {
          filename: this.rawFile.name,
          content_type: this.rawFile.type || 'application/octet-stream',
        },
      });

      try {
        this.uploadRequest = UploadedFile.uploadToS3(this.rawFile, fileUploadRequest);
      } catch (error: any) {
        this.isFailed = true;
        this.isUploading = false;
        this.isUploaded = false;

        if (error?.message === 'File size exceeded') {
          this.errorCode = 'file_too_big';
        } else {
          this.errorCode = 'file_error';
        }
      }

      if (this.uploadRequest) {
        this.uploadRequest
          .on(
            'progress',
            action(({ percent }: { percent: number }) => {
              this.percent = percent;
            }),
          )
          .then(
            action(async () => {
              this.isUploading = false;
              this.isUploaded = true;

              const backendConfirmation = await this.api.FilesFileUploadRequestUploadedApi.create({
                id: fileUploadRequest.id,
              });

              if (backendConfirmation.status === 'UPLOADED') {
                if (this.onUploaded) this.onUploaded(backendConfirmation.file_upload, this);
              }
            }),
          )
          .catch(
            action(() => {
              this.isUploading = false;
              this.isFailed = true;
              this.errorCode = 'upload_error';
            }),
          );
      }

      return this.uploadRequest;
    }
    return undefined;
  }

  @action.bound retry() {
    this.abort();
    this.upload();
  }
}

export default UploadedFile;
